import ApiClient from "./client";
import ProjectTemplate from "@/models/project-template";
import ProjectTemplateTask from "@/models/project-template-task";
import ProjectTemplateTaskGroup from "@/models/project-template-task-group";
import parseDate from "@/helpers/parseDate";
import endOfDuration from "@/helpers/endOfDuration";
import formatDate from "@/helpers/formatDate";
import { jsonToFormData } from "@/helpers/formData";

function parseTemplate(data: any): ProjectTemplate {
  if (!data.hasOwnProperty("id")) throw new Error("Invalid data");
  return { ...data, taskGroups: data.taskGroups.map(parseTaskGroup) };
}

function parseTaskGroup(data: any): ProjectTemplateTaskGroup {
  if (!data.hasOwnProperty("id")) throw new Error("Invalid data");
  return { ...data, tasks: data.tasks.map(parseTask) };
}

function parseTask(data: any): ProjectTemplateTask {
  if (!data.hasOwnProperty("id")) throw new Error("Invalid data");
  const start = parseDate(data.start);
  return { ...data, start, end: endOfDuration(start, data.duration) };
}

export default class ProjectTemplateApi {
  public constructor(private client: ApiClient) {}

  public async getAll(): Promise<ProjectTemplate[]> {
    const data = await this.client.get("get_project_templates.php");
    return data.map(parseTemplate);
  }

  public async get(id: number | string) {
    const templates = await this.getAll();
    return templates.find(t => String(t.id) === String(id));
  }

  public async save(template: ProjectTemplate): Promise<number> {
    const d = new FormData();
    d.append("id", String(template.id));
    d.append("title", template.title);
    const res = await this.client.post("save_project_template.php", d);
    return res.id;
  }

  public async destroy(id: number): Promise<void> {
    const d = new FormData();
    d.append("id", String(id));
    await this.client.post("delete_project_template.php", d);
  }

  public async createTask(task: ProjectTemplateTask) {
    task.start = formatDate(task.start as Date);
    await this.client.post(
      "save_project_template_task.php",
      jsonToFormData(task)
    );
  }

  public async updateTask(task: ProjectTemplateTask) {
    await this.createTask(task);
  }

  public async deleteTask(id: number) {
    const d = new FormData();
    d.append("id", String(id));
    await this.client.post("delete_project_template_task.php", d);
  }

  public async createTaskGroup(group: ProjectTemplateTaskGroup) {
    const d = new FormData();
    d.append("id", String(group.id));
    d.append("taskTypeId", String(group.taskTypeId));
    d.append("projectTemplateId", String(group.projectTemplateId));
    const res = await this.client.post(
      "save_project_template_task_group.php",
      d
    );
    return res.id;
  }

  public async updateTaskGroup(group: ProjectTemplateTaskGroup) {
    await this.createTaskGroup(group);
  }

  public async deleteTaskGroup(id: number) {
    const d = new FormData();
    d.append("id", String(id));
    await this.client.post("delete_project_template_task_group.php", d);
  }

  public async sortTaskGroups(templateId: number, groupIds: number[]) {
    const d = new FormData();
    d.append("id", String(templateId));
    groupIds.forEach(gid => {
      d.append("taskGroupIds[]", String(gid));
    });
    await this.client.post("sort_project_template_task_groups.php", d);
  }

  public async duplicate(id: number): Promise<number> {
    const d = new FormData();
    d.append("id", String(id));
    const res = await this.client.post("duplicate_project_template.php", d);
    return res.id;
  }

  public async saveProjectTemplateTaskDuration(data: any) {
    return await this.client
      .post("save_project_template_task_duration.php", jsonToFormData(data))
      .then(res => res)
      .catch(err => err[0]);
  }
}
